// Provider registry + dispatcher. This is the only module UI code should
// import — everything it needs (bootstrap, normalize, upstream calls,
// capabilities, display helpers) flows through here.
//
// Contents:
//   - registry + bootstrap_providers()
//   - resolver helpers (internal)
//   - dispatch functions (search_all, get_seatmap, apply_seats, …)
//   - capability introspection
//   - re-exports of Provider / AmadeusProvider / KyteProvider
//
// Canonical FlightOffer / Selections / Amenity typedefs live in ./types.js
// and have no runtime footprint — they're loaded by tooling only.

import { log } from '@/utilities/commons';
import { service_handler } from '@/services';
import Provider from './base/provider';
import AmadeusProvider from './amadeus';
import KyteProvider from './kyte';

// ---------------------------------------------------------------------------
// Registry — key → ProviderClass
// ---------------------------------------------------------------------------

const AVAILABLE_PROVIDERS = {
  amadeus: AmadeusProvider,
  kyte: KyteProvider,
};

const init_provider = (key, { capabilities = {}, enabled } = {}) => {
  const Cls = AVAILABLE_PROVIDERS[key];
  return new Cls({
    providerId: key,
    capabilities: capabilities || {},
    enabled,
  });
};

const build_registry = (config = {}) => {
  const next = {};
  Object.keys(AVAILABLE_PROVIDERS).forEach((key) => {
    const cfg = (config && config[key]) || {};
    next[key] = init_provider(key, cfg);
  });
  return next;
};

let ACTIVE_PROVIDERS = build_registry();

/**
 * Initialize providers at app bootstrap using the liveflight providers config.
 * Safe to call multiple times — each call replaces the active registry.
 *
 * Expected config shape:
 *   {
 *     amadeus: { enabled: true,  capabilities: { seatSelection: false, ... } },
 *     kyte:    { enabled: false, capabilities: {} }
 *   }
 *
 * Every key in AVAILABLE_PROVIDERS is instantiated and registered regardless
 * of `enabled`. `enabled: false` does NOT remove a provider — it gates the
 * search → book flow (search, offer details, seats, bags, ancillaries, book)
 * while get_confirmation / normalize / validate and the pure read/display
 * helpers stay live, so bookings created while the provider was on still
 * confirm and render. A missing `enabled` falls back to the provider class's
 * DEFAULT_ENABLED (true for amadeus, false for every other provider);
 * missing capabilities fall back to DEFAULT_CAPABILITIES.
 *
 * @param {Object<string, {enabled?: boolean, capabilities?: Object}>} [config]
 * @returns {Object<string, Provider>} the newly-active registry
 */
export const bootstrap_providers = (config = {}) => {
  ACTIVE_PROVIDERS = build_registry(config);
  return { ...ACTIVE_PROVIDERS };
};

// ---------------------------------------------------------------------------
// Resolver helpers (internal)
// ---------------------------------------------------------------------------

// Every normalized FlightOffer is stamped with `_provider` by the
// concrete normalize(). A missing stamp means the offer was deserialised
// without going through normalize (lost during a localStorage rehydrate,
// a hand-rolled fixture, a future provider that forgot to stamp).
// Refuse to guess: silently dispatching to a default would route the
// wrong cache for display_carrier and only crash later inside that
// provider's normalize.
const ensure_provider = (raw) => {
  if (raw && typeof raw._provider === 'string' && raw._provider) {
    return raw._provider;
  }
  throw new Error(
    '[providers] FlightOffer is missing `_provider`; cannot dispatch'
  );
};

const resolve = (provider_id) => {
  const provider = ACTIVE_PROVIDERS[provider_id];
  if (!provider) {
    throw new Error(`Provider not active in registry: ${provider_id}`);
  }
  return provider;
};

// `source` accepts either a provider_id string or a FlightOffer (dispatcher
// reads `_provider` from the offer).
const resolve_from_source = (source) => {
  const provider_id =
    typeof source === 'string' ? source : ensure_provider(source);
  return resolve(provider_id);
};

/**
 * Dispatch a search → book flow action only when the source's provider is
 * registered AND enabled. get_confirmation / validate / normalize and the
 * pure read/display helpers bypass this — a disabled provider must still
 * parse and confirm bookings it created while on.
 *
 * @param {string|FlightOffer} source        - provider_id or offer to resolve
 * @param {function(Provider): *} on_enabled - runs the action on the provider
 * @param {function(Provider): *} on_disabled - disabled outcome: a resolved
 *   no-op for reads the UI calls unconditionally, a rejection
 *   (reject_disabled) for actions that must fail loudly
 * @returns {*} whatever the chosen callback returns
 */
const resolve_if_enabled = (source, on_enabled, on_disabled) => {
  const provider = resolve_from_source(source);
  if (provider.is_enabled()) {
    return on_enabled(provider);
  }
  return on_disabled(provider);
};

const reject_disabled = (op) => (provider) =>
  Promise.reject(
    new Error(
      `[providers.${op}] ${provider.providerId} not available (disabled)`
    )
  );

// Registered AND enabled — the ids the search → book flow fans out over.
export const list_enabled_providers = () =>
  Object.keys(ACTIVE_PROVIDERS).filter((id) =>
    ACTIVE_PROVIDERS[id].is_enabled()
  );

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

/**
 * Run a flight search against a single provider with the generic UI payload.
 * Resolves to a normalized FlightOffer[]; a disabled provider resolves to [].
 *
 * @param {string} provider_id  - registry key (e.g. "amadeus", "kyte")
 * @param {Object} payload      - the generic search request body
 * @returns {Promise<FlightOffer[]>}
 */
export const search = (provider_id, payload) =>
  resolve_if_enabled(
    provider_id,
    (provider) => provider.search(payload),
    () => Promise.resolve([])
  );

/**
 * Fan-out search across every enabled provider in parallel and flatten the
 * results into a single normalized FlightOffer[]. This is the single entry
 * point the results page uses — provider-aware code stays inside the
 * provider modules.
 *
 * Uses Promise.allSettled so one provider rejecting (missing endpoint,
 * 5xx, parse error) never empties the whole results page. Rejected
 * providers are logged and skipped; the returned list is the flattened
 * union of every fulfilled provider's offers.
 *
 * @param {Object} payload - the generic search request body, identical for
 *                           every provider; each provider's `search` knows
 *                           how to translate it for its own service handler.
 * @returns {Promise<FlightOffer[]>}
 */
export const search_all = (payload) => {
  const entries = list_enabled_providers().map((id) => ({
    id,
    promise: Promise.resolve().then(() => ACTIVE_PROVIDERS[id].search(payload)),
  }));
  return Promise.allSettled(entries.map((entry) => entry.promise)).then(
    (settled) => {
      const offers = [];
      settled.forEach((result, index) => {
        const { id } = entries[index];
        if (result.status === 'fulfilled') {
          const list = Array.isArray(result.value) ? result.value : [];
          list.forEach((offer) => {
            offers.push(offer);
          });
          return;
        }
        log(`[providers.search_all] ${id} search failed`, result.reason);
      });
      return offers;
    }
  );
};

/**
 * Fan-out no-results suggestion fetches across every enabled provider in
 * parallel and merge their normalized offers into the two canonical
 * buckets the UI renders: `by_radius` (nearby-airport alternatives) and
 * `by_time_window` (alternative-date alternatives). Providers that don't
 * advertise the `searchSuggestions` capability resolve to empty buckets,
 * so this is safe to call unconditionally.
 *
 * Uses Promise.allSettled — symmetric with search_all — so one provider
 * rejecting (5xx, parse error, missing endpoint) never empties the
 * combined suggestion set. Rejected providers are logged and skipped.
 *
 * @param {Object} payload - the generic search request body
 * @returns {Promise<{ by_radius: FlightOffer[], by_time_window: FlightOffer[] }>}
 */
export const search_all_suggestions = (payload) => {
  const entries = list_enabled_providers().map((id) => ({
    id,
    promise: Promise.resolve().then(() =>
      ACTIVE_PROVIDERS[id].search_suggestion(payload)
    ),
  }));
  return Promise.allSettled(entries.map((entry) => entry.promise)).then(
    (settled) => {
      const by_radius = [];
      const by_time_window = [];
      settled.forEach((result, index) => {
        const { id } = entries[index];
        if (result.status === 'fulfilled') {
          const {
            by_radius: provider_radius = [],
            by_time_window: provider_time_window = [],
          } = result.value || {};
          if (Array.isArray(provider_radius)) {
            by_radius.push(...provider_radius);
          }
          if (Array.isArray(provider_time_window)) {
            by_time_window.push(...provider_time_window);
          }
          return;
        }
        log(
          `[providers.search_all_suggestions] ${id} suggestion failed`,
          result.reason
        );
      });
      return { by_radius, by_time_window };
    }
  );
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const get_known_carriers = (source) =>
  resolve_from_source(source).get_known_carriers();

export const get_known_locations = (source) =>
  resolve_from_source(source).get_known_locations();

// Offer-aware traveler counts. Each provider decides how its taxonomy
// maps to adults — the default walks travelerPricings filtering by
// travelerType === 'ADULT'. Used wherever the UI renders "1 per adult".
export const count_adults = (flight_offer) =>
  resolve_from_source(flight_offer).count_adults(flight_offer);

// ---------------------------------------------------------------------------
// Upstream calls — one entry point per provider-dispatched op. Capability-
// gated ops resolve silently to null / {} when the provider does not
// advertise the capability.
// ---------------------------------------------------------------------------

export const get_offer_details = (flight_offer) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.get_offer_details(flight_offer),
    () => Promise.resolve(flight_offer)
  );

// Automated check-in offer — capability-gated. Providers without
// `automatedCheckIn` resolve to unavailable, so the checkout page can probe
// unconditionally. `citizenship` is the traveler's country (ISO code);
// the provider defaults it when omitted.
//
// Resolves `{ available, price }`. A null `price` means the service is free —
// the only shape production quotes today — so the panel keeps its free copy
// and nothing is added to the totals.
export const get_automated_checkin_offer = (flight_offer, citizenship) =>
  resolve_if_enabled(
    flight_offer,
    (provider) =>
      provider.get_automated_checkin_offer(flight_offer, citizenship),
    () => Promise.resolve({ available: false, price: null })
  );

export const get_seatmap = (flight_offer, options) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.get_seatmap(flight_offer, options),
    () => Promise.resolve(null)
  );

export const get_upsell = (flight_offer) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.get_upsell(flight_offer),
    () => Promise.resolve(null)
  );

// Fare-choice helpers — every operation on the branded-fare upsell list
// flows through the provider so the shape-specific reads (brand id,
// filter heuristics, pick action) stay one place.
export const get_branded_fare = (flight_offer) =>
  resolve_from_source(flight_offer).get_branded_fare(flight_offer);

export const get_branded_fare_label = (flight_offer) =>
  resolve_from_source(flight_offer).get_branded_fare_label(flight_offer);

export const find_selected_upsell = (upsells, current_flight_offer) =>
  resolve_from_source(current_flight_offer).find_selected_upsell(
    upsells,
    current_flight_offer
  );

export const filter_visible_upsells = (params = {}) =>
  resolve_from_source(params.default_offer).filter_visible_upsells(params);

export const choose_upsell = (flight_offer) =>
  resolve_from_source(flight_offer).choose_upsell(flight_offer);

export const get_extra_bags = (flight_offer) =>
  resolve_from_source(flight_offer).get_extra_bags(flight_offer);

// Apply the FlexibleBagsState selection onto the offer and reprice if the
// provider supports it. Returns a Promise<FlightOffer> regardless of
// provider — Amadeus reprices via flightOffersPrice; Kyte stages onto the
// raw offer and resolves immediately (selections sit until book-time).
export const add_extra_bags = (flight_offer, flexible_bags) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.add_extra_bags(flight_offer, flexible_bags),
    () => Promise.resolve(flight_offer)
  );

// Read every chargeableCheckedBags currently attached to the offer for
// confirm / checkout display. Returns
// `[{ travelerId, segmentId, bag: ChargeableCheckedBags }, …]`.
export const read_extra_bags = (flight_offer) =>
  resolve_from_source(flight_offer).read_extra_bags(flight_offer);

// Offer-shape bag operations — capability-gated on the provider.
// add_charged_bags: returns a new flight_offer with the selected
//                   flexible bags applied. Sync; does NOT reprice — use
//                   add_extra_bags above to reprice after applying.
// get_bags:         per-itinerary tally + per-traveler breakdown.
// has_chargeable_bags / has_included_bags: booleans for a given traveler.
export const add_charged_bags = (flight_offer, flexible_bags) =>
  resolve_from_source(flight_offer).add_charged_bags(
    flight_offer,
    flexible_bags
  );

export const get_bags = (flight_offer, itinerary_index) =>
  resolve_from_source(flight_offer).get_bags(flight_offer, itinerary_index);

export const has_chargeable_bags = (flight_offer, traveler_id) =>
  resolve_from_source(flight_offer).has_chargeable_bags(
    flight_offer,
    traveler_id
  );

export const has_included_bags = (flight_offer, traveler_id) =>
  resolve_from_source(flight_offer).has_included_bags(
    flight_offer,
    traveler_id
  );

// Seat selection — provider-owned offer mutation.
// Canonical SeatSelection / Selections typedefs live in ./types.js.
export const apply_seats = (flight_offer, selections) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.apply_seats(flight_offer, selections),
    reject_disabled('apply_seats')
  );

export const read_seats = (
  flight_offer,
  segment_id,
  seat_options_for_segment
) =>
  resolve_from_source(flight_offer).read_seats(
    flight_offer,
    segment_id,
    seat_options_for_segment
  );

export const validate_seats_priced = (
  flight_offer,
  selections,
  priced_flight_offer
) =>
  resolve_from_source(flight_offer).validate_seats_priced(
    flight_offer,
    selections,
    priced_flight_offer
  );

// Each provider decides from its own corner of the fare whether the
// booking can advance without an explicit seat pick for every non-
// held-infant traveler. UI gates the /ancillaries → /checkout edge on
// this + a per-traveler-per-segment completeness check. See
// providers/{amadeus,kyte}/seats.js for the per-provider logic.
export const seats_are_mandatory = (flight_offer) =>
  resolve_from_source(flight_offer).seats_are_mandatory(flight_offer);

// Ancillaries-as-step — capability-gated. Defaults pass through so
// callers can dispatch unconditionally; providers without the
// capability resolve to no-op shapes documented in base/provider.js.
export const book_held = (flight_offer, traveler_details, contact_details) =>
  resolve_if_enabled(
    flight_offer,
    (provider) =>
      provider.book_held(flight_offer, traveler_details, contact_details),
    reject_disabled('book_held')
  );

// Rejects (not `{}`) when disabled so ancillaries.vue's allSettled
// total-failure check sees both calls down and takes the
// on_total_failure escape to checkout instead of rendering an empty
// wizard off a truthy empty envelope.
export const fetch_ancillaries = (flight_offer, booking_id) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.fetch_ancillaries(flight_offer, booking_id),
    reject_disabled('fetch_ancillaries')
  );

export const retrieve = (flight_offer, booking_id) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.retrieve(flight_offer, booking_id),
    reject_disabled('retrieve')
  );

export const book_ancillaries = (
  flight_offer,
  booking_id,
  selections,
  catalog
) =>
  resolve_if_enabled(
    flight_offer,
    (provider) =>
      provider.book_ancillaries(flight_offer, booking_id, selections, catalog),
    reject_disabled('book_ancillaries')
  );

/**
 * Final-checkout call. `request` is the canonical CheckoutRequest
 * (providers/types.js) — UI hands provider-agnostic state slices and the
 * resolved provider builds its own wire body internally. Kyte needs to
 * read `validatingAirlineCodes` off the offer, so unlike most dispatched
 * ops `source` here must be the FlightOffer, never a provider_id string.
 *
 *   Amadeus: builds `{ bookingData: { flightCreateOrderBody, payments,
 *            live_price }, ...guest }` and POSTs to `book`.
 *   Kyte:    builds `{ bookingId, airlineCode }` from `request.heldBooking`
 *            and `flight_offer.validatingAirlineCodes[0]` and POSTs to
 *            `kyte.checkout`.
 *
 * @param {FlightOffer}     flight_offer
 * @param {CheckoutRequest} request
 */
export const book = (flight_offer, request) =>
  resolve_if_enabled(
    flight_offer,
    (provider) => provider.book(flight_offer, request),
    reject_disabled('book')
  );

// Map `/confirm` envelope key → provider id. The response is the most
// reliable source of truth for which provider parser to run; the
// store-side flight_offer is a fallback only — it can be stale or
// missing on deep links / refreshes / post-payment landings.
const VOUCHER_KEY_TO_PROVIDER = {
  kyteVoucher: 'kyte',
  flightVoucher: 'amadeus',
};

const detect_provider_from_voucher = (body) => {
  const safe = body || {};
  const key = Object.keys(VOUCHER_KEY_TO_PROVIDER).find(
    (k) => safe[k] && typeof safe[k] === 'object'
  );
  return key ? VOUCHER_KEY_TO_PROVIDER[key] : null;
};

// Ordered list of provider ids to try when the /confirm response has no
// voucher-key match. Source-stamped first, amadeus as the historical
// default, then every other active provider so a kyte-only (or future-
// provider-only) deploy still has a chance to parse the body.
const fallback_confirmation_candidates = (source) => {
  const tried = {};
  const ordered = [];
  const try_add = (id) => {
    if (id && ACTIVE_PROVIDERS[id] && !tried[id]) {
      ordered.push(id);
      tried[id] = true;
    }
  };
  if (source && typeof source._provider === 'string') {
    try_add(source._provider);
  }
  try_add('amadeus');
  Object.keys(ACTIVE_PROVIDERS).forEach((id) => try_add(id));
  return ordered;
};

// Run the shared `/confirm` BE call once, then route the response body
// to the right provider parser based on its envelope key. `source`
// (typically the offer in the store) is used only as a fallback when
// the response carries neither known voucher key — e.g. an early BE
// error before the booking shape is set.
export const get_confirmation = (source, request) =>
  service_handler('confirm', request).then((response) => {
    const { data: body = response || {} } = response || {};
    const provider_id = detect_provider_from_voucher(body);
    if (provider_id) {
      return resolve(provider_id).get_confirmation(body);
    }
    const candidates = fallback_confirmation_candidates(source);
    return candidates.reduce(
      (acc, id) =>
        acc.catch(() =>
          Promise.resolve().then(() =>
            ACTIVE_PROVIDERS[id].get_confirmation(body)
          )
        ),
      Promise.reject(
        new Error('[providers.get_confirmation] no candidate provider')
      )
    );
  });

// ---------------------------------------------------------------------------
// Capability introspection
// ---------------------------------------------------------------------------

export const has_capability = (provider_id, name) => {
  const provider = ACTIVE_PROVIDERS[provider_id];
  return !!provider && provider.has_capability(name);
};

export const get_capabilities = (provider_id) => {
  const provider = ACTIVE_PROVIDERS[provider_id];
  return provider ? provider.get_capabilities() : {};
};

// Offer-aware variant — reads the capability for the offer's own provider.
export const has_capability_for = (flight_offer, name) =>
  has_capability(ensure_provider(flight_offer), name);

// ---------------------------------------------------------------------------
// Introspection helpers (useful for dev badges / tests)
// ---------------------------------------------------------------------------

export const list_active_providers = () => Object.keys(ACTIVE_PROVIDERS);

export { AVAILABLE_PROVIDERS, Provider, AmadeusProvider, KyteProvider };
